import type {
  BillingCycleHorizon,
  CreateCreditCardRequest,
  CreateExpenseRequest,
  CreateRecurringExpenseRequest,
  CreateRecurringInflowRequest,
  CreateWindfallInflowRequest,
  CreditCardProfileDto,
  EntitlementMode,
  PaymentMethodType,
  ReceiptDocumentType,
  ReceiptExtractionResult,
  RecurringValidityMode,
  SolTemporalKpiSnapshot,
  UpdateCreditCardRequest,
  UpdateCyclePolicyRequest,
  UpdateExpenseRequest,
  UpdateRecurringExpenseMonthRequest,
  UpdateRecurringExpenseRequest,
  UpdateRecurringInflowRequest,
  UpdateWindfallInflowRequest,
} from '@economi/domain-contracts';

export type {
  BillingCycleHorizon,
  CreateCreditCardRequest,
  CreditCardProfileDto,
  PaymentMethodType,
  ReceiptDocumentType,
  RecurringValidityMode,
  UpdateCreditCardRequest,
  UpdateExpenseRequest,
};

export interface ApiHealthResult {
  readonly online: boolean;
  readonly latencyMs: number;
  readonly entitlementMode?: EntitlementMode;
  readonly allowedPilotEmails?: readonly [string, string];
}

const API_BASE = '/api/v1';
const SESSION_STORAGE_KEY = 'economi_auth_session_v1';

export function getAuthHeaders(): Record<string, string> {
  try {
    const raw =
      window.localStorage.getItem(SESSION_STORAGE_KEY) ??
      window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return {};
    const session = JSON.parse(raw) as AuthenticatedUserSession;
    if (!session?.user?.email) return {};
    return {
      'X-User-Id': session.user.id ?? '',
      'X-User-Email': session.user.email,
      Authorization: `Bearer ${session.sessionToken ?? ''}`,
    };
  } catch {
    return {};
  }
}

export async function fetchSyncedCycleStateFromApi<T>(): Promise<{
  ok: boolean;
  hasData: boolean;
  updatedAtIso: string | null;
  cycleState: T | null;
}> {
  try {
    const response = await fetch(`${API_BASE}/state/sync`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!response.ok) {
      return { ok: false, hasData: false, updatedAtIso: null, cycleState: null };
    }
    const data = (await response.json()) as {
      ok?: boolean;
      hasData?: boolean;
      updatedAtIso?: string | null;
      cycleState?: T | null;
    };
    return {
      ok: Boolean(data.ok ?? true),
      hasData: Boolean(data.hasData && data.cycleState),
      updatedAtIso: data.updatedAtIso ?? null,
      cycleState: data.cycleState ?? null,
    };
  } catch {
    return { ok: false, hasData: false, updatedAtIso: null, cycleState: null };
  }
}

export async function pushSyncedCycleStateToApi<T>(
  cycleState: T
): Promise<{ syncedWithServer: boolean; updatedAtIso?: string }> {
  try {
    const response = await fetch(`${API_BASE}/state/sync`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ cycleState }),
    });
    if (!response.ok) {
      return { syncedWithServer: false };
    }
    const data = (await response.json().catch(() => ({}))) as {
      updatedAtIso?: string;
    };
    return {
      syncedWithServer: true,
      updatedAtIso: data.updatedAtIso,
    };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function checkApiHealth(): Promise<ApiHealthResult> {
  const start = performance.now();
  try {
    const response = await fetch(`${API_BASE}/health`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!response.ok) {
      return { online: false, latencyMs: Math.round(performance.now() - start) };
    }
    const data = (await response.json()) as {
      entitlementMode?: EntitlementMode;
      entitlement?: {
        mode?: EntitlementMode;
        allowedPilotEmails?: string[];
      };
    };
    const emails = data.entitlement?.allowedPilotEmails;
    return {
      online: true,
      latencyMs: Math.max(1, Math.round(performance.now() - start)),
      entitlementMode: data.entitlementMode ?? data.entitlement?.mode,
      allowedPilotEmails:
        emails && emails.length >= 2 ? [emails[0]!, emails[1]!] : undefined,
    };
  } catch {
    return { online: false, latencyMs: 0 };
  }
}

export async function fetchServerKpiSnapshot(): Promise<SolTemporalKpiSnapshot | null> {
  try {
    const response = await fetch(`${API_BASE}/telemetry/kpis`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!response.ok) return null;
    return (await response.json()) as SolTemporalKpiSnapshot;
  } catch {
    return null;
  }
}

export async function postExpenseToApi(
  payload: CreateExpenseRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/expenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': `web-exp-${Date.now()}`,
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function registerExpenseApi(
  payload: CreateExpenseRequest & {
    readonly paymentMethod?: PaymentMethodType;
    readonly creditCardId?: string | null;
    readonly billingCycleHorizon?: BillingCycleHorizon;
  }
): Promise<{ syncedWithServer: boolean }> {
  return postExpenseToApi(payload);
}

export async function fetchCreditCardsFromApi(): Promise<
  ReadonlyArray<CreditCardProfileDto>
> {
  try {
    const response = await fetch(`${API_BASE}/credit-cards`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!response.ok) return [];
    const data = (await response.json()) as {
      creditCards?: ReadonlyArray<CreditCardProfileDto>;
    };
    return data.creditCards ?? [];
  } catch {
    return [];
  }
}

export async function upsertCreditCardApi(
  payload: CreateCreditCardRequest
): Promise<{ syncedWithServer: boolean; card?: CreditCardProfileDto }> {
  try {
    const response = await fetch(`${API_BASE}/credit-cards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      return { syncedWithServer: false };
    }
    const data = (await response.json().catch(() => ({}))) as {
      card?: CreditCardProfileDto;
    };
    return {
      syncedWithServer: true,
      card: data.card,
    };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function updateCreditCardApi(
  cardId: string,
  payload: UpdateCreditCardRequest
): Promise<{ syncedWithServer: boolean; card?: CreditCardProfileDto }> {
  try {
    const response = await fetch(
      `${API_BASE}/credit-cards/${encodeURIComponent(cardId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      return { syncedWithServer: false };
    }
    const data = (await response.json().catch(() => ({}))) as {
      card?: CreditCardProfileDto;
    };
    return {
      syncedWithServer: true,
      card: data.card,
    };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function deleteCreditCardApi(
  cardId: string
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/credit-cards/${encodeURIComponent(cardId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function postCyclePolicyToApi(
  payload: UpdateCyclePolicyRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/cycles/active/policy`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function postRecurringInflowToApi(
  payload: CreateRecurringInflowRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/inflows/recurring`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function postWindfallInflowToApi(
  payload: CreateWindfallInflowRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/inflows/windfall`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function postRecurringExpenseToApi(
  payload: CreateRecurringExpenseRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/expenses/recurring`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function createRecurringExpenseApi(
  payload: CreateRecurringExpenseRequest & {
    readonly validityMode?: RecurringValidityMode;
    readonly expirationDateIso?: string | null;
    readonly isCreditCardExpense?: boolean;
    readonly creditCardId?: string | null;
  }
): Promise<{ syncedWithServer: boolean }> {
  return postRecurringExpenseToApi(payload);
}

export async function patchRecurringExpenseMonthToApi(
  ruleId: string,
  payload: UpdateRecurringExpenseMonthRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/expenses/recurring/${encodeURIComponent(ruleId)}/month`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function patchRecurringExpenseToApi(
  ruleId: string,
  payload: UpdateRecurringExpenseRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/expenses/recurring/${encodeURIComponent(ruleId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function updateRecurringExpenseApi(
  ruleId: string,
  payload: UpdateRecurringExpenseRequest & {
    readonly validityMode?: RecurringValidityMode;
    readonly expirationDateIso?: string | null;
    readonly isCreditCardExpense?: boolean;
    readonly creditCardId?: string | null;
  }
): Promise<{ syncedWithServer: boolean }> {
  return patchRecurringExpenseToApi(ruleId, payload);
}

export async function deleteRecurringExpenseFromApi(
  ruleId: string
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/expenses/recurring/${encodeURIComponent(ruleId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function patchRecurringInflowToApi(
  ruleId: string,
  payload: UpdateRecurringInflowRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/inflows/recurring/${encodeURIComponent(ruleId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function deleteRecurringInflowFromApi(
  ruleId: string
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/inflows/recurring/${encodeURIComponent(ruleId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function patchWindfallInflowToApi(
  inflowId: string,
  payload: UpdateWindfallInflowRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/inflows/windfall/${encodeURIComponent(inflowId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function deleteWindfallInflowFromApi(
  inflowId: string
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/inflows/windfall/${encodeURIComponent(inflowId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function deleteExpenseFromApi(
  expenseId: string
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/expenses/${encodeURIComponent(expenseId)}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function updateExpenseApi(
  expenseId: string,
  payload: UpdateExpenseRequest
): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(
      `${API_BASE}/expenses/${encodeURIComponent(expenseId)}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export async function resetCleanSystemViaApi(): Promise<{ syncedWithServer: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/system/reset-clean`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({}),
    });
    return { syncedWithServer: response.ok };
  } catch {
    return { syncedWithServer: false };
  }
}

export interface ScanUploadedReceiptRequest {
  readonly fileName: string;
  readonly mimeType: string;
  readonly imageBase64?: string;
  readonly rawOcrHintText?: string;
  readonly merchantHint?: string;
  readonly totalAmountSolesHint?: number;
  readonly documentTypeHint?: ReceiptDocumentType;
}

export interface ScanUploadedReceiptResponse {
  readonly extraction: ReceiptExtractionResult;
  readonly matchedCreditCardId?: string | null;
  readonly storedObject?: {
    readonly r2ObjectKey: string;
    readonly bucketName: string;
    readonly publicPreviewUrl: string;
  };
}

export async function scanUploadedReceiptViaApi(
  payload: ScanUploadedReceiptRequest
): Promise<{ ok: true; data: ScanUploadedReceiptResponse } | { ok: false; error: string }> {
  try {
    const response = await fetch(`${API_BASE}/receipts/scan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errBody = (await response.json().catch(() => ({}))) as { message?: string };
      return {
        ok: false,
        error: errBody.message ?? `Error HTTP ${response.status} al escanear el comprobante.`,
      };
    }
    const data = (await response.json()) as {
      extraction?: ReceiptExtractionResult;
      matchedCreditCardId?: string | null;
      storedObject?: {
        r2ObjectKey: string;
        bucketName: string;
        publicPreviewUrl: string;
      };
    } & ReceiptExtractionResult;
    const rawExtraction = data.extraction ?? data;
    const extraction: ReceiptExtractionResult = {
      ...rawExtraction,
      cardLastFourDigits: rawExtraction.cardLastFourDigits ?? null,
    };
    return {
      ok: true,
      data: {
        extraction,
        matchedCreditCardId: data.matchedCreditCardId ?? null,
        storedObject: data.storedObject,
      },
    };
  } catch {
    return {
      ok: false,
      error:
        'No se pudo conectar con la API en http://localhost:4000. Asegúrate de tener activo "pnpm dev:api".',
    };
  }
}

export const scanReceiptFromApi = scanUploadedReceiptViaApi;

export async function scanReceiptViaApi(
  templateIdOrFileName: string
): Promise<ReceiptExtractionResult | null> {
  const res = await scanUploadedReceiptViaApi({
    fileName: `${templateIdOrFileName}.webp`,
    mimeType: 'image/webp',
  });
  return res.ok ? res.data.extraction : null;
}

export interface AuthenticatedUserSession {
  readonly sessionToken: string;
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly fullName: string;
    readonly phone?: string | null;
    readonly occupation?: string | null;
  };
}

export async function signupUserViaApi(payload: {
  fullName: string;
  email: string;
  phone?: string;
  occupation?: string;
  appBaseUrl?: string;
}): Promise<
  | {
      ok: true;
      email: string;
      fullName: string;
      emailDeliveryProvider: string;
      message: string;
    }
  | {
      ok: false;
      code: string;
      message: string;
    }
> {
  try {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      message?: string;
      email?: string;
      fullName?: string;
      emailDeliveryProvider?: string;
    };

    if (!response.ok) {
      return {
        ok: false,
        code: data.error ?? 'ERR_SIGNUP_FAILED',
        message:
          data.message ??
          'No se pudo completar el registro. Verifica tus datos.',
      };
    }

    return {
      ok: true,
      email: data.email ?? payload.email,
      fullName: data.fullName ?? payload.fullName,
      emailDeliveryProvider: data.emailDeliveryProvider ?? 'RESEND_CLOUD',
      message:
        data.message ??
        'Hemos enviado un enlace a tu correo para crear tu contraseña.',
    };
  } catch {
    return {
      ok: false,
      code: 'ERR_API_OFFLINE',
      message:
        'No se pudo conectar con la API en http://localhost:4000. Asegúrate de tener corriendo "pnpm dev:api".',
    };
  }
}

export async function setPasswordViaApi(payload: {
  email: string;
  token: string;
  password: string;
}): Promise<
  | {
      ok: true;
      session: AuthenticatedUserSession;
    }
  | {
      ok: false;
      code: string;
      message: string;
    }
> {
  try {
    const response = await fetch(`${API_BASE}/auth/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      sessionToken?: string;
      user?: AuthenticatedUserSession['user'];
    };

    if (!response.ok || !data.sessionToken || !data.user) {
      return {
        ok: false,
        code: data.error ?? 'ERR_SET_PASSWORD_FAILED',
        message:
          data.message ?? 'No se pudo configurar la contraseña con este enlace.',
      };
    }

    return {
      ok: true,
      session: {
        sessionToken: data.sessionToken,
        user: data.user,
      },
    };
  } catch {
    return {
      ok: false,
      code: 'ERR_API_OFFLINE',
      message:
        'No se pudo conectar con la API en http://localhost:4000. Asegúrate de tener corriendo "pnpm dev:api".',
    };
  }
}

export async function loginUserViaApi(payload: {
  email: string;
  password: string;
}): Promise<
  | {
      ok: true;
      session: AuthenticatedUserSession;
    }
  | {
      ok: false;
      code: string;
      message: string;
    }
> {
  try {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      sessionToken?: string;
      user?: AuthenticatedUserSession['user'];
    };

    if (!response.ok || !data.sessionToken || !data.user) {
      return {
        ok: false,
        code: data.error ?? 'ERR_LOGIN_FAILED',
        message: data.message ?? 'No se pudo iniciar sesión.',
      };
    }

    return {
      ok: true,
      session: {
        sessionToken: data.sessionToken,
        user: data.user,
      },
    };
  } catch {
    return {
      ok: false,
      code: 'ERR_API_OFFLINE',
      message:
        'No se pudo conectar con la API en http://localhost:4000. Asegúrate de tener corriendo "pnpm dev:api".',
    };
  }
}
